import { Image as ApiImage } from '@/types';
import { DetailedHTMLProps, ImgHTMLAttributes, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import NoImage from '@/../img/no-image.svg';

type ImageProps = {
    image?: ApiImage;
    alt: string;
    loading?: 'lazy' | 'eager';
    className?: string;
} & DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export const Image = ({ image, alt, loading = 'lazy', className }: ImageProps) => {
    const imageRef = useRef<HTMLImageElement | null>(null);
    const [imageError, setImageError] = useState(false);

    const srcSet = useMemo(() => {
        if (!image || !image.responsive) {
            return '';
        }

        const sizes = image.responsive.sizes.map((imageSize) => {
            return `${imageSize.url} ${imageSize.width}w`;
        });

        sizes.push(`${image.responsive.thumbnail} 32w`);

        return sizes.join(', ');
    }, [image]);

    useLayoutEffect(() => {
        setSizesAttribute();
        window.addEventListener('resize', setSizesAttribute);

        return () => {
            window.removeEventListener('resize', setSizesAttribute);
        };
    }, []);

    const setSizesAttribute = () => {
        if (imageRef.current) {
            const image = imageRef.current;
            const size = image.clientWidth;

            image.sizes = Math.ceil((size / window.innerWidth) * 100) + 'vw';
        }
    };

    if (!image || imageError) {
        return (
            <img
                alt={alt}
                loading={loading}
                src={NoImage}
                className={twMerge('object-cover', className)}
            />
        );
    }

    if (!image.responsive) {
        return (
            <img
                ref={imageRef}
                alt={alt}
                loading={loading}
                src={image.originalUrl}
                className={twMerge('object-cover', className)}
                onError={() => setImageError(true)}
            />
        );
    }

    return (
        <img
            ref={imageRef}
            alt={alt}
            loading={loading}
            src={image.originalUrl}
            srcSet={srcSet}
            width={image.responsive.originalWidth}
            height={image.responsive.originalHeight}
            sizes={'1px'}
            className={twMerge('object-cover', className)}
            onError={() => setImageError(true)}
        />
    );
};
